import React, { useEffect, useState } from 'react';
import axios from '../helpers/axiosConfig';
import { useParams } from 'react-router-dom';
import getBaseUrl from '../helpers/utility';

const FichePersonnel = () => {
  const { id } = useParams();
  const baseUrl = getBaseUrl();
  const [personnel, setPersonnel] = useState(null);

  useEffect(() => {
    axios
      .get(`personnel/show/${id}`)
      .then((response) => setPersonnel(response.data))
      .catch((error) => console.error('Erreur:', error));
  }, [id]);

  if (!personnel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-400 text-lg font-semibold animate-pulse">Chargement...</div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('Fr-fr', options);
  };

  const getGrade = () => {
    switch (personnel.arme) {
      case 'AIR': return personnel.grade?.gradeair;
      case 'MER': return personnel.grade?.grademer;
      default: return personnel.grade?.name;
    }
  };

  // Composant réutilisable pour les sections
  const SectionHeader = ({ left, right }) => (
    <div className="bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-3 flex items-center justify-between">
      <span className="font-bold text-white text-sm uppercase tracking-wider">{left}</span>
      {right && (
        <>
          <span className="text-white/60">—</span>
          <span className="font-bold text-white text-sm uppercase tracking-wider">{right}</span>
        </>
      )}
    </div>
  );

  const TableHead = ({ cols }) => (
    <thead>
      <tr className="bg-slate-800">
        {cols.map((col, i) => (
          <th key={i} className="px-3 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider whitespace-nowrap">
            {col}
          </th>
        ))}
      </tr>
    </thead>
  );

  const Row = ({ children, index }) => (
    <tr className={`border-b border-slate-100 hover:bg-amber-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
      {children}
    </tr>
  );

  const Td = ({ children }) => (
    <td className="px-3 py-2 text-sm text-slate-700">{children}</td>
  );

  const InfoItem = ({ label, value }) => (
    value ? (
      <div className="flex gap-2 py-2 border-b border-slate-100 last:border-0">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-36 shrink-0">{label}</span>
        <span className="text-sm text-slate-800 font-medium">{value}</span>
      </div>
    ) : null
  );

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10 mt-5">
      <div className="max-w-6xl mx-auto">

        {/* Titre */}
        <div className="text-center mb-8">
          <span className="bg-amber-100 text-amber-800 text-xs font-bold px-4 py-1 rounded-full uppercase tracking-widest">
            DCCA — Dossier Personnel
          </span>
          <h2 className="text-2xl font-bold text-slate-800 mt-3">Fiche de Renseignement</h2>
        </div>

        {/* CARTE INFOS PERSONNELLES */}
        <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm mb-5">
          <SectionHeader left="Informations Personnelles" />
          <div className="p-5 flex flex-col md:flex-row gap-6">

            {/* Photo */}
            <div className="shrink-0 flex flex-col items-center gap-3">
              <img
                src={`${baseUrl}/images/personnels/${personnel.filename}`}
                alt="photo"
                className="w-40 h-48 object-cover rounded-xl shadow-md border-4 border-amber-100"
              />
              <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full uppercase">
                {getGrade()}
              </span>
              <p className="text-sm font-bold text-slate-800 text-center">{personnel.nomprenom}</p>
            </div>

            {/* Infos col 1 */}
            <div className="flex-1">
              {personnel.compagnie && (
                <div className="bg-slate-50 rounded-xl p-3 mb-3 border border-slate-200">
                  <InfoItem label="Brigade" value={personnel.compagnie.bataillon.brigade.name} />
                  <InfoItem label="Bataillon" value={personnel.compagnie.bataillon.name} />
                  <InfoItem label="Compagnie" value={personnel.compagnie.name} />
                </div>
              )}
              <InfoItem label="Nom et Prénom" value={personnel.nomprenom} />
              <InfoItem label="Date de Naissance" value={formatDate(personnel.datenaisse)} />
              <InfoItem label="Etsouservice" value={personnel.etsouservice?.name} />
              <InfoItem label="Adresse Actuelle" value={personnel.adresseactuelle} />
              <InfoItem label="CIN" value={personnel.cin} />
              <InfoItem label="Sexe" value={personnel.sexe} />
              <InfoItem label="Téléphone" value={personnel.telephone} />
              <InfoItem label="WhatsApp" value={personnel.whatsapp} />
            </div>

            {/* Infos col 2 */}
            <div className="flex-1">
              <InfoItem label="Groupe Sanguin" value={personnel.groupesanguin} />
              <InfoItem label="Groupe Ethnique" value={personnel.groupeethnique} />
              <InfoItem label="Religion" value={personnel.religion} />
              <InfoItem label="Affection Actuelle" value={personnel.affectionactuelle} />
              <InfoItem label="Matricule" value={personnel.matricule} />
              <InfoItem label="Matricule Finance" value={personnel.MatriculeFinance} />
              <InfoItem label="Indice" value={personnel.indice} />
              <InfoItem label="Interruption du" value={personnel.interruptiondu} />
              <InfoItem label="Au" value={personnel.au} />
              <InfoItem label="Sortant École" value={personnel.sortantecole} />
              <InfoItem label="E-poketra" value={personnel.epoketra} />
              <InfoItem label="N° Carte Verte" value={personnel.numerocarteverte} />
            </div>

          </div>
        </div>

        {/* AFFECTATIONS + ENFANTS */}
        <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm mb-5">
          <SectionHeader left="Affectations Successives" right="Enfants" />
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <div className="overflow-x-auto p-4">
              <table className="min-w-full text-sm">
                <TableHead cols={['Lieu Affect.', 'Fonction', 'Date effet', 'Décision']} />
                <tbody>
                  {personnel.affectations?.map((a, i) => (
                    <Row key={i} index={i}>
                      <Td>{a.lieuaffect}</Td>
                      <Td>{a.fonctiontenue}</Td>
                      <Td>{formatDate(a.dateeffet)}</Td>
                      <Td>{a.decisionaffectation}</Td>
                    </Row>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="overflow-x-auto p-4">
              <table className="min-w-full text-sm">
                <TableHead cols={['Nom et Prénoms', 'Date Naiss.', 'Sexe', 'Observation']} />
                <tbody>
                  {personnel.enfant?.map((e, i) => (
                    <Row key={i} index={i}>
                      <Td>{e.nomprenom}</Td>
                      <Td>{formatDate(e.datenaisse)}</Td>
                      <Td>{e.sexe}</Td>
                      <Td>{e.observation}</Td>
                    </Row>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* AVANCEMENTS + DECORATIONS */}
        <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm mb-5">
          <SectionHeader left="Avancements" right="Décorations" />
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <div className="overflow-x-auto p-4">
              <table className="min-w-full text-sm">
                <TableHead cols={['Statut', 'Référence', 'Date d\'effet']} />
                <tbody>
                  {personnel.avancement?.map((a, i) => (
                    <Row key={i} index={i}>
                      <Td>{a.statu}</Td>
                      <Td>{a.reference}</Td>
                      <Td>{formatDate(a.dateeffet)}</Td>
                    </Row>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="overflow-x-auto p-4">
              <table className="min-w-full text-sm">
                <TableHead cols={['Décoration', 'Année', 'Décret / Arrêté']} />
                <tbody>
                  {personnel.decorations?.map((d, i) => (
                    <Row key={i} index={i}>
                      <Td>{d.name}</Td>
                      <Td>{d.PersonnelDecoration.dateObtention}</Td>
                      <Td>{d.PersonnelDecoration.decretouarrete}</Td>
                    </Row>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ECOLE + PERMIS */}
        <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm mb-5">
          <SectionHeader left="École" right="Autres Compétences" />
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <div className="overflow-x-auto p-4">
              <table className="min-w-full text-sm">
                <TableHead cols={['Intitulé', 'Établissement', 'Diplôme', 'Année']} />
                <tbody>
                  {personnel.ecole?.map((e, i) => (
                    <Row key={i} index={i}>
                      <Td>{e.intitule}</Td>
                      <Td>{e.etablissement}</Td>
                      <Td>{e.diplome}</Td>
                      <Td>{e.annee > 0 ? e.annee : ''}</Td>
                    </Row>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 flex flex-col gap-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Permis de conduire</p>
              <InfoItem label="N° Permis" value={personnel.NumPermis} />
              <InfoItem label="Délivré le" value={personnel.permisDelivrele} />
              <InfoItem label="Lieu" value={personnel.lieuDelivrance} />
              <InfoItem label="Catégorie" value={personnel.permisCategorie} />
            </div>
          </div>
        </div>

        {/* INFORMATIQUE + LANGUES */}
        <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm mb-5">
          <SectionHeader left="Informatique" right="Langues" />
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
            <div className="p-4 flex flex-col gap-2">
              <InfoItem label="Bureautique" value={personnel.bureautique ? 'OUI' : ''} />
              <InfoItem label="Autres" value={personnel.autres} />
            </div>
            <div className="p-4 flex flex-col gap-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Langues maîtrisées</p>
              <InfoItem label="Français" value={personnel.francais} />
              <InfoItem label="Anglais" value={personnel.anglais} />
              <InfoItem label="Autres langues" value={personnel.autresLangue} />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FichePersonnel;